import { useState, useRef, useCallback } from "react";
import { Mic, MicOff, Loader2, X } from "lucide-react";

const API_BASE = "http://localhost:8765";

interface Props {
  onTranscribed: (text: string) => void;
  language?: string;
}

export function AudioInput({ onTranscribed, language = "zh" }: Props) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState("");
  const [previewText, setPreviewText] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    setError("");
    setPreviewText("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });

      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setRecording(true);
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === "NotAllowedError") {
        setError("请允许麦克风权限");
      } else {
        setError("无法启动录音");
      }
    }
  }, [language]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  }, [recording]);

  async function transcribeAudio(audioBlob: Blob) {
    setTranscribing(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.webm");
      formData.append("model", "base");
      formData.append("language", language);

      const res = await fetch(`${API_BASE}/api/stt`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "语音识别失败");
      }

      const data = await res.json();
      if (data.success && data.data.text) {
        setPreviewText(data.data.text);
        onTranscribed(data.data.text);
      } else {
        setError("未识别到语音内容");
      }
    } catch (e: unknown) {
      // 如果 STT 服务不可用，提示用户
      setError(e instanceof Error ? e.message : "语音识别失败，请检查 STT 服务");
    } finally {
      setTranscribing(false);
    }
  }

  function handleConfirm() {
    if (previewText) {
      onTranscribed(previewText);
      setPreviewText("");
    }
  }

  function handleCancel() {
    setPreviewText("");
  }

  return (
    <div className="flex items-center gap-2">
      {/* 录音控制 */}
      <button
        onClick={recording ? stopRecording : startRecording}
        disabled={transcribing}
        className={`p-2 rounded-xl transition-all ${
          recording
            ? "bg-red-500/20 text-red-400 animate-pulse border border-red-500/30"
            : "bg-slate-800 text-slate-400 hover:text-rose-400 border border-slate-700 hover:border-rose-500/30"
        } disabled:opacity-50`}
        title={recording ? "停止录音" : "开始录音"}
      >
        {transcribing ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : recording ? (
          <MicOff className="w-4 h-4" />
        ) : (
          <Mic className="w-4 h-4" />
        )}
      </button>

      {/* 录音状态指示 */}
      {recording && (
        <span className="text-xs text-red-400 animate-pulse">录音中...</span>
      )}
      {transcribing && (
        <span className="text-xs text-slate-400">识别中...</span>
      )}

      {/* 识别结果预览 */}
      {previewText && !recording && !transcribing && (
        <div className="flex items-center gap-1 bg-slate-800 rounded-lg px-2 py-1">
          <span className="text-xs text-slate-300 max-w-[200px] truncate">
            {previewText}
          </span>
          <button
            onClick={handleCancel}
            className="p-0.5 text-slate-500 hover:text-red-400"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {error && (
        <span className="text-[10px] text-red-400">{error}</span>
      )}
    </div>
  );
}
